import {generateUUID} from '../utils/uuid'
import {getDeviceInfo} from '../utils/device'
import {apiFetch} from './apiBase'

const USUARIO_BOT_ID = Number(import.meta.env.VITE_DEFAULT_USUARIO_BOT_ID || import.meta.env.VITE_DEFAULT_BOT_ID)
export const STORAGE_SESSION = 'session_id_bot'
export const STORAGE_TOKEN = 'token_auth_bot'
const STORAGE_TOKEN_BOT = 'token_auth_bot_id'

function parseJwtPayload(token) {
    if (!token || typeof token !== 'string') return null
    const parts = token.split('.')
    if (parts.length < 2) return null
    try {
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
        const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4)
        const json = atob(padded)
        return JSON.parse(json)
    } catch (_) {
        return null
    }
}

function isTokenExpired(token, skewMs = 30000) {
    const payload = parseJwtPayload(token)
    if (!payload || !payload.exp) return true
    const expMs = payload.exp * 1000
    return Date.now() + skewMs >= expMs
}

function qsParams() {
    const urlParams = new URLSearchParams(window.location.search)
    const p = (k) => urlParams.get(k) || null
    return {
        nomeCampanha: p('nome_campanha'),
        tituloHtml: p('nome_html'),
        dataInicioCampanha: p('data_inicio_campanha'),
        tituloLandingPage: p('data_landing_page'),
        fonteUtm: p('fonte_utm'),
        midiaUtm: p('midia_utm'),
        termoUtm: p('termo_utm'),
        conteudoUtm: p('conteudo_utm'),
    }
}

export class BotApi {
    constructor() {
        this.sessionId = localStorage.getItem(STORAGE_SESSION) || generateUUID()
        localStorage.setItem(STORAGE_SESSION, this.sessionId)
        this.token = null
    }

    static resetSession() {
        localStorage.removeItem(STORAGE_SESSION)
        localStorage.removeItem(STORAGE_TOKEN)
        localStorage.removeItem(STORAGE_TOKEN_BOT)
        localStorage.removeItem('token_auth_bot_slug')
    }

    async _fetch(path, {method = 'GET', body, headers = {}, blob = false, _retry = false} = {}) {
        const h = {'Content-Type': 'application/json', ...headers}

        const doRequest = async () => apiFetch(path, {method, headers: h, body})
        let res = await doRequest()

        // If token expired (403), try to refresh token once and retry
        if (res.status === 403) {
            const isAuthRequest = /sessoes\/token/.test(path)
            const hasAuthHeader = !!h.Authorization
            if (!_retry && hasAuthHeader && !isAuthRequest) {
                try {
                    // Force new token
                    localStorage.removeItem(STORAGE_TOKEN)
                    this.token = null
                    await this.login(true)
                    if (hasAuthHeader) {
                        h.Authorization = `Bearer ${this.token}`
                    }
                    res = await apiFetch(path, {method, headers: h, body})
                } catch (_) {
                    // fall through to error handling below
                }
            }
        }

        if (!res.ok) {
            const t = await res.text().catch(() => '')
            throw new Error(`HTTP ${res.status}: ${t}`)
        }
        return blob ? res.blob() : res.json()
    }

    async login(force = false) {
        const cached = !force && localStorage.getItem(STORAGE_TOKEN)
        const cachedBot = localStorage.getItem(STORAGE_TOKEN_BOT)
        if (cached && cachedBot === String(USUARIO_BOT_ID) && !isTokenExpired(cached)) {
            this.token = cached
            return cached
        }
        if (cached) {
            localStorage.removeItem(STORAGE_TOKEN)
        }
        if (cachedBot && cachedBot !== String(USUARIO_BOT_ID)) {
            localStorage.removeItem(STORAGE_TOKEN_BOT)
        }
        const j = await this._fetch(`/sessoes/token?usuarioBotId=${USUARIO_BOT_ID}`, {method: 'POST', body: JSON.stringify({}), _retry: true})
        this.token = j.token
        localStorage.setItem(STORAGE_TOKEN, this.token)
        localStorage.setItem(STORAGE_TOKEN_BOT, String(USUARIO_BOT_ID))
        return this.token
    }

    async trackView() {
        const device = getDeviceInfo()
        const payload = {
            clienteId: null,
            sessionId: this.sessionId,
            telefone: '',
            dataVisualizacao: new Date().toISOString(),
            url: window.location.href, ...qsParams(),
            nomeDispositivo: device.nomeDispositivo,
            tipoDispositivo: device.tipoDispositivo,
            navegador: device.navegador,
        }
        const headers = this.token ? {Authorization: `Bearer ${this.token}`} : {}
        return this._fetch('/sessoes/trafego', {
            method: 'POST', body: JSON.stringify(payload), headers,
        }).catch(() => null)
    }

    async startSession({sessionId = this.sessionId} = {}) {
        if (!this.token) {
            await this.login()
        }
        const payload = {usuarioBotId: USUARIO_BOT_ID, sessionId}
        const data = await this._fetch('/sessoes/start', {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {Authorization: `Bearer ${this.token}`},
            _retry: true,
        })
        if (data?.sessionId && data.sessionId !== this.sessionId) {
            this.sessionId = data.sessionId
            try {
                localStorage.setItem(STORAGE_SESSION, data.sessionId)
            } catch (_) {
                // ignore
            }
        }
        return data
    }

    async sendText(valor) {
        return this._fetch(`/sessoes/${this.sessionId}/responder`, {
            method: 'POST', body: JSON.stringify({tipo: 'TEXT_INPUT', valor}), headers: {Authorization: `Bearer ${this.token}`},
        })
    }

    async sendButtons(valor) {
        return this._fetch(`/sessoes/${this.sessionId}/responder`, {
            method: 'POST', body: JSON.stringify({tipo: 'BUTTONS', valor}), headers: {Authorization: `Bearer ${this.token}`},
        })
    }

    async sendList(chaves) {
        return this._fetch(`/sessoes/${this.sessionId}/responder`, {
            method: 'POST', body: JSON.stringify({tipo: 'LIST', chaves}), headers: {Authorization: `Bearer ${this.token}`},
        })
    }

    async uploadDocumento(file) {
        const formData = new FormData()
        formData.append('file', file)
        const url = `/sessoes/${this.sessionId}/documento`
        const h = {Authorization: `Bearer ${this.token}`}
        let res = await apiFetch(url, {method: 'POST', headers: h, body: formData})
        if (res.status === 403) {
            try {
                await this.login(true)
                res = await apiFetch(url, {method: 'POST', headers: {Authorization: `Bearer ${this.token}`}, body: formData})
            } catch (_) {
            }
        }
        if (!res.ok) throw new Error('Falha ao enviar documento')
        return res.json()
    }

    async uploadSelfie(file) {
        const formData = new FormData()
        formData.append('file', file)
        const url = `/sessoes/${this.sessionId}/selfie`
        const h = {Authorization: `Bearer ${this.token}`}
        let res = await apiFetch(url, {method: 'POST', headers: h, body: formData})
        if (res.status === 403) {
            try {
                await this.login(true)
                res = await apiFetch(url, {method: 'POST', headers: {Authorization: `Bearer ${this.token}`}, body: formData})
            } catch (_) {
            }
        }
        if (!res.ok) throw new Error('Falha ao enviar selfie')
        return res.json()
    }

    async descadastrarSessao(sessaoId) {
        const session = sessaoId || this.sessionId
        if (!session) {
            throw new Error('Sessão inválida')
        }

        if (!this.token) {
            await this.login()
        }

        const endpoint = `/sessoes/${encodeURIComponent(session)}/descadastra`

        const request = async () => apiFetch(endpoint, {
            method: 'GET', headers: {Authorization: `Bearer ${this.token}`},
        })

        let res = await request()

        if (res.status === 403) {
            await this.login(true)
            res = await request()
        }

        if (!res.ok) {
            const text = await res.text().catch(() => '')
            const suffix = text ? `: ${text}` : ''
            throw new Error(`Falha ao descadastrar sessão (HTTP ${res.status})${suffix}`)
        }

        return res
    }

    downloadPdfBase64(base64, name = 'arquivo.pdf') {
        const b64 = (base64 || '').split(',').pop();
        const bin = atob(b64);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        const blob = new Blob([arr], {type: 'application/pdf'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name.endsWith('.pdf') ? name : `${name}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }
}
